/**
 * CS330 student-only layout. Intentionally separate from AppLayout (the
 * User Study / TA layout) so changes here cannot affect the User Study
 * interface and vice versa. Reuses shared shell components (SessionNavigator,
 * ContentArea) but owns its own right-panel structure and CSS.
 *
 * Route: /student-app/:deviceId?/:tab?
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import styles from './StudentAppLayout.module.css';
import SessionNavigator from '../session-navigator/SessionNavigator';
import ContentArea from '../content-area/ContentArea';
import StudentRightPanel from '../student/StudentRightPanel';

const StudentAppLayout = () => {
    const { deviceId } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [sessions, setSessions] = useState([]);
    const [sessionDevices, setSessionDevices] = useState({});
    const [currentUser, setCurrentUser] = useState(null);

    const [navigatorCollapsed, setNavigatorCollapsed] = useState(false);
    const [transcriptOpen, setTranscriptOpen] = useState(false);

    useEffect(() => {
        if (searchParams.get('transcript') === 'open' && deviceId) {
            setTranscriptOpen(true);
            setNavigatorCollapsed(true);
            searchParams.delete('transcript');
            setSearchParams(searchParams, { replace: true });
        }
    }, [deviceId, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleTranscriptToggle = (open) => {
        setTranscriptOpen(open);
        if (open) {
            setNavigatorCollapsed(true);
        } else {
            setNavigatorCollapsed(false);
        }
    };

    const handleNavigatorCollapse = (collapsed) => {
        setNavigatorCollapsed(collapsed);
        if (!collapsed) setTranscriptOpen(false);
    };

    useEffect(() => {
        (async () => {
            try {
                const meRes = await fetch('/api/v1/me');
                if (meRes.status === 401) {
                    navigate('/login', { replace: true });
                    return;
                }
                const me = meRes.ok ? await meRes.json() : null;
                // Only students should land here. Bounce others to /app.
                if (me && me.role !== 'student') {
                    navigate('/app', { replace: true });
                    return;
                }
                setCurrentUser(me);
                await fetchSessionsAndDevices();
            } catch (_) {
                await fetchSessionsAndDevices();
            }
        })();
    }, []);

    const fetchSessionsAndDevices = async () => {
        try {
            // Students always use the participation-filtered endpoint.
            const res = await fetch('/api/v1/me/sessions');
            if (!res.ok) return;
            const sessionsData = await res.json();
            setSessions(sessionsData);

            const devicesMap = {};
            await Promise.all(sessionsData.map(async s => {
                try {
                    const devRes = await fetch(`/api/v1/sessions/${s.id}/devices`);
                    devicesMap[s.id] = devRes.ok ? await devRes.json() : [];
                } catch (_) {
                    devicesMap[s.id] = [];
                }
            }));
            setSessionDevices(devicesMap);
        } catch (_) {}
    };

    const activeDeviceInfo = useMemo(() => {
        if (!deviceId) return null;
        for (const [sessionId, devices] of Object.entries(sessionDevices)) {
            const device = devices.find(d => String(d.id) === String(deviceId));
            if (device) {
                const session = sessions.find(s => String(s.id) === String(sessionId));
                return { device, session, sessionId: parseInt(sessionId) };
            }
        }
        return null;
    }, [deviceId, sessionDevices, sessions]);

    return (
        <div className={styles.layout}>
            <div className={styles.leftPanel}>
                <SessionNavigator
                    sessions={sessions}
                    sessionDevices={sessionDevices}
                    activeDeviceId={deviceId}
                    collapsed={navigatorCollapsed}
                    onCollapsedChange={handleNavigatorCollapse}
                    basePath="/student-app"
                />
                <ContentArea
                    deviceId={deviceId}
                    deviceInfo={activeDeviceInfo}
                    transcriptOpen={transcriptOpen}
                    onTranscriptToggle={handleTranscriptToggle}
                    readOnly={true}
                />
            </div>
            <div className={styles.rightPanel}>
                <StudentRightPanel
                    currentUser={currentUser}
                    sessionDeviceId={deviceId ? parseInt(deviceId) : null}
                    onUserUpdate={setCurrentUser}
                />
            </div>
        </div>
    );
};

export default StudentAppLayout;
